import { normalizeReservationBusinessRules } from './reservationBusinessRules';
import { reservationTimeLimitReason, type ReservationTimeLimitSettings, type ReservationTimeLimitReason } from './reservationTimeLimits';

export type ReservationModificationReason = ReservationTimeLimitReason | 'DECREASE_DEADLINE_PASSED' | 'INCREASE_DEADLINE_PASSED' | 'CANCELLATION_DEADLINE_PASSED';
// New requests use current unit settings; existing promised terms remain unchanged.
// Notice is elapsed time. Exact equality is allowed, including across DST.
export const reservationModificationPolicy = (settings: ReservationTimeLimitSettings, input: {
  originalHeadcount: number; headcount: number; originalStart: Date; originalEnd: Date;
  start: Date; end: Date; nowMs: number; status: string;
}) => {
  const timeChanged = input.originalStart.getTime() !== input.start.getTime() || input.originalEnd.getTime() !== input.end.getTime();
  const increase = input.headcount > input.originalHeadcount;
  const decrease = input.headcount < input.originalHeadcount;
  const result = (reason: ReservationModificationReason | null, requiresApproval = false, notifyUnit = true) => ({ reason, requiresApproval, notifyUnit, timeChanged, increase, decrease });
  try {
    const rules = normalizeReservationBusinessRules(settings.businessRules);
    if (![input.originalHeadcount, input.headcount].every(n => Number.isSafeInteger(n) && n >= 1 && n <= 10000) ||
        ![input.originalStart.getTime(), input.originalEnd.getTime(), input.start.getTime(), input.end.getTime(), input.nowMs].every(Number.isFinite)) return result('INVALID_TIME_LIMIT_INPUT');
    if (input.originalStart.getTime() < input.nowMs || input.start.getTime() < input.nowMs) return result('RESERVATION_START_PASSED');
    if (timeChanged) {
      const reason = reservationTimeLimitReason(settings, input.start, input.nowMs);
      if (reason) return result(reason);
    }
    if (increase) {
      const notice = rules.modifications.increaseNoticeHours;
      if (notice === null) {
        const reason = reservationTimeLimitReason(settings, input.start, input.nowMs);
        if (reason) return result(reason);
      } else if (input.originalStart.getTime() - input.nowMs < notice * 3600000) return result('INCREASE_DEADLINE_PASSED');
    }
    if (decrease && rules.modifications.decreaseNoticeHours !== null && input.originalStart.getTime() - input.nowMs < rules.modifications.decreaseNoticeHours * 3600000) return result('DECREASE_DEADLINE_PASSED');
    const requiresApproval = increase || (decrease && rules.modifications.decreaseRequiresApproval) || (timeChanged && input.status === 'confirmed');
    return result(null, requiresApproval, increase ? rules.notifications.notifyUnitOnIncreaseRequest : decrease ? rules.notifications.notifyUnitOnDecrease : true);
  } catch { return result('INVALID_TIME_LIMIT_INPUT'); }
};

export const reservationCancellationReason = (settings: ReservationTimeLimitSettings, start: Date, nowMs: number): ReservationModificationReason | null => {
  try {
    const notice = normalizeReservationBusinessRules(settings.businessRules).modifications.cancellationNoticeHours;
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(nowMs)) return 'INVALID_TIME_LIMIT_INPUT';
    if (start.getTime() < nowMs) return 'RESERVATION_START_PASSED';
    return notice !== null && start.getTime() - nowMs < notice * 3600000 ? 'CANCELLATION_DEADLINE_PASSED' : null;
  } catch { return 'INVALID_TIME_LIMIT_INPUT'; }
};

export const reservationModificationMessage = (reason: ReservationModificationReason, locale: 'hu' | 'en'): string => {
  const copy = locale === 'hu'
    ? 'Az online módosítás határideje lejárt. A gyorsabb ügyintézéshez hívd az egységet; a foglalásod változatlan maradt.'
    : 'The online change deadline has passed. Call the venue for faster assistance; your booking is unchanged.';
  return reason === 'INVALID_TIME_LIMIT_INPUT' ? (locale === 'hu' ? 'A módosítás szabályai nem ellenőrizhetők. Frissítsd az oldalt.' : 'Change rules could not be checked. Refresh the page.') : copy;
};
